import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../services/apiService';
import { showToast } from './Toast';
import ConfirmModal from './ConfirmModal';
import { DSButton, DSIcon, DSModal } from '../design-system/primitives';
import { compressImage, MAX_UPLOAD_BYTES } from '../utils/imageCompress';
import '../pages/taskManagementShared.css';

const MAX_PER_ENTRY = 10;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Screenshots on a task, template, suggestion or completion.
 *
 * Two modes:
 *   - Saved: pass `owner` and `ownerId`. Loads what is attached, and uploads or
 *     removes straight away, as far as the server says this viewer may.
 *   - Not saved yet (a create form): pass `pendingFiles` and `onPendingChange`
 *     instead of an id. Files are held here until the form saves, then the form
 *     calls uploadPendingAttachments with the new id.
 *
 * Images are compressed in the browser first, the same as score photos, and
 * anything still over 3 MB is turned away before it is sent.
 */

async function prepareFiles(fileList) {
  const ready = [];
  for (const file of Array.from(fileList)) {
    let prepared = file;
    try {
      prepared = await compressImage(file, `${(file.name || 'screenshot').replace(/\.[^.]+$/, '')}.jpg`);
    } catch {
      // Compression is an optimisation, not a gate. Send the original and let the checks below decide.
    }
    if (!ACCEPTED_TYPES.includes(prepared.type)) {
      showToast(`${file.name || 'That file'} is not an image that can be attached`, 'error');
    } else if (prepared.size > MAX_UPLOAD_BYTES) {
      showToast(`${file.name || 'That image'} is over 3 MB even after shrinking`, 'error');
    } else {
      ready.push(prepared);
    }
  }
  return ready;
}

export async function uploadPendingAttachments(owner, ownerId, files) {
  if (!files || files.length === 0) return;
  await apiService.uploadTaskAttachments(owner, ownerId, files);
}

function useObjectUrl(loader, deps) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let created = null;
    loader()
      .then((blob) => {
        if (cancelled || !blob) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return url;
}

// Image files need the auth header, so they are fetched as blobs rather than linked by URL.
function SavedImage({ attachmentId, alt, className }) {
  const url = useObjectUrl(
    () => apiService.getTaskAttachmentFile(attachmentId).then((res) => res.data),
    [attachmentId]
  );
  return url ? <img src={url} alt={alt} className={className} /> : <div className={`${className} tm-attachment-loading`} />;
}

function PendingImage({ file, alt, className }) {
  const url = useObjectUrl(() => Promise.resolve(file), [file]);
  return url ? <img src={url} alt={alt} className={className} /> : null;
}

function TaskAttachments({ owner, ownerId, pendingFiles, onPendingChange, title = 'Screenshots', onChange }) {
  const isPending = !ownerId;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (isPending) return;
    try {
      const res = await apiService.getTaskAttachments(owner, ownerId);
      setData(res.data);
    } catch {
      setData({ attachments: [], canAdd: false });
    }
  }, [isPending, owner, ownerId]);

  useEffect(() => {
    load();
  }, [load]);

  const items = isPending
    ? (pendingFiles || []).map((file, index) => ({ key: `p${index}`, file, index, canDelete: true, name: file.name }))
    : (data?.attachments || []).map((a) => ({ key: a.id, id: a.id, canDelete: a.canDelete, name: a.originalName, uploaderName: a.uploaderName }));
  const canAdd = isPending ? Boolean(onPendingChange) : Boolean(data?.canAdd);
  const room = MAX_PER_ENTRY - items.length;

  if (!isPending && !data) return null;
  if (!canAdd && items.length === 0) return null;

  const handleFiles = async (event) => {
    const chosen = Array.from(event.target.files || []);
    // Clear the input so picking the same file again still fires a change.
    event.target.value = '';
    if (chosen.length === 0) return;
    if (chosen.length > room) {
      showToast(`Up to ${MAX_PER_ENTRY} screenshots can be attached here`, 'error');
      return;
    }

    setBusy(true);
    try {
      const ready = await prepareFiles(chosen);
      if (ready.length === 0) return;
      if (isPending) {
        onPendingChange([...(pendingFiles || []), ...ready]);
      } else {
        await apiService.uploadTaskAttachments(owner, ownerId, ready);
        await load();
        onChange?.();
        showToast(ready.length === 1 ? 'Screenshot attached' : `${ready.length} screenshots attached`, 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not attach the screenshots', 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (item) => {
    if (isPending) {
      onPendingChange(pendingFiles.filter((_, i) => i !== item.index));
      return;
    }
    setRemoving(item);
  };

  const confirmRemove = async () => {
    try {
      setBusy(true);
      await apiService.deleteTaskAttachment(removing.id);
      await load();
      onChange?.();
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not remove the screenshot', 'error');
    } finally {
      setBusy(false);
      setRemoving(null);
    }
  };

  return (
    <div className="tm-attachments">
      <strong>{title}</strong>
      {items.length > 0 && (
        <div className="tm-attachment-grid">
          {items.map((item) => (
            <div key={item.key} className="tm-attachment">
              <button
                type="button"
                className="tm-attachment-open"
                onClick={() => setViewing(item)}
                aria-label={`View ${item.name || 'screenshot'}`}
              >
                {isPending
                  ? <PendingImage file={item.file} alt={item.name || 'Screenshot'} className="tm-attachment-thumb" />
                  : <SavedImage attachmentId={item.id} alt={item.name || 'Screenshot'} className="tm-attachment-thumb" />}
              </button>
              {item.canDelete && (
                <button
                  type="button"
                  className="tm-attachment-remove"
                  onClick={() => removeItem(item)}
                  disabled={busy}
                  aria-label={`Remove ${item.name || 'screenshot'}`}
                >
                  <DSIcon name="x" size="sm" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {canAdd && room > 0 && (
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleFiles}
          />
          <DSButton
            type="button"
            appearance="outline"
            tone="secondary"
            size="sm"
            leadingIcon={<DSIcon name="plus" />}
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            {busy ? 'Attaching...' : 'Attach screenshots'}
          </DSButton>
          <div className="tm-suggestion-meta">JPEG, PNG or WebP, up to {MAX_PER_ENTRY}. Large images are shrunk before upload.</div>
        </div>
      )}

      {viewing && (
        <DSModal title={viewing.name || 'Screenshot'} size="lg" onClose={() => setViewing(null)}>
          <div className="tm-attachment-full-wrap">
            {isPending
              ? <PendingImage file={viewing.file} alt={viewing.name || 'Screenshot'} className="tm-attachment-full" />
              : <SavedImage attachmentId={viewing.id} alt={viewing.name || 'Screenshot'} className="tm-attachment-full" />}
          </div>
          {viewing.uploaderName && <div className="tm-suggestion-meta">Attached by {viewing.uploaderName}</div>}
        </DSModal>
      )}
      {removing && (
        <ConfirmModal
          title="Remove Screenshot"
          message="Remove this screenshot? This cannot be undone."
          confirmLabel="Remove"
          danger
          submitting={busy}
          onConfirm={confirmRemove}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

export default TaskAttachments;
